import { useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProjectWidget, UpdateProjectWidgetBody } from '@vd/types';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@vd/ui';
import { AlertTriangle, Check, Copy, Globe, Users, UsersRound, X } from 'lucide-react';
import { api } from '../../lib/api';
import { copyToClipboard } from '../../lib/clipboard';
import { useBillingSummary } from '../billing/useBillingSummary';
import { AllowedSitesEditor } from './AllowedSitesEditor';
import { isLegacyAnySite, NEEDS_A_SITE } from './siteApproval';
import { BigSwitch } from '../settings/BigSwitch';
import { WidgetVerificationPanel } from './WidgetVerificationPanel';
import { ReportWayArt, type ReportWay } from './WidgetIllustrations';
import { widgetSnippet } from './snippet';
import { WidgetTestReport } from './WidgetTestReport';
import { WIDGET_SETUP_SCREEN } from '../../lib/screenNames';
import styles from './WidgetSetupWizard.module.css';

/**
 * Setting the widget up, one decision at a time (TOADO-579).
 *
 * The settings are four tabs whose options depend on each other without
 * saying so: pointing and recording need allowed sites, recording needs a
 * paid plan, and the snippet is best checked before the widget is on, which
 * works since TOADO-577. The
 * wizard asks what the owner is trying to do and sets the switches to match:
 * who reports, where it goes, what people can do, the snippet and its check,
 * then turning it on. Appearance stays out of it; the defaults are fine and
 * the tabs are there for later.
 *
 * Each step saves through PATCH /projects/:id/widget, the route the tabs use,
 * so the two can never disagree about what is set. The widget stays off until
 * the last step, so a wizard closed halfway leaves settings that serve nobody
 * yet, and the tabs show exactly what was chosen.
 */

/**
 * The steps, each with its screen name for pins (TOADO-701) beside its label,
 * so adding or moving a step cannot leave a name on the wrong one. The step
 * list and the buttons under it are the dialog's, on every step. A name is
 * stored with every pin placed under it: never rename one.
 */
const STEP_DEFS = [
  { label: 'Who reports', screen: 'who-reports' },
  { label: 'Where it goes', screen: 'where-it-goes' },
  { label: 'What people can do', screen: 'what-people-can-do' },
  { label: 'Add it to your site', screen: 'add-to-site' },
  { label: 'Turn it on', screen: 'turn-on' },
  // A test report the owner sends themselves, and where it lands (TOADO-975).
  { label: 'Try it', screen: 'try-it' },
] as const;
const STEPS = STEP_DEFS.map((definition) => definition.label);
const STEP_SCREENS = STEP_DEFS.map((definition) => `${WIDGET_SETUP_SCREEN}/${definition.screen}`);

export function WidgetSetupWizard({ open, projectId, webOrigin, onClose }: {
  open: boolean;
  projectId: string;
  /** Public web origin, used to render the snippet. */
  webOrigin?: string;
  onClose: () => void;
}) {
  const widgetQuery = useQuery({
    queryKey: ['widget', projectId],
    // Lazily creates the widget row on first open, as the tabs do.
    queryFn: () => api.getWidget(projectId),
    enabled: open,
  });
  return (
    // Wide enough for "What people can do" as three columns (TOADO-596).
    <Modal open={open} onClose={onClose} width={880} labelledBy="widget-setup-title">
      <ModalHeader id="widget-setup-title">Set up the bug report widget</ModalHeader>
      {widgetQuery.data ? (
        // Mounted once the widget is loaded, so each step starts from what is saved.
        <WizardSteps widget={widgetQuery.data} projectId={projectId} webOrigin={webOrigin} onClose={onClose} />
      ) : (
        <ModalBody>
          <p className={styles.muted}>{widgetQuery.isError ? 'The widget could not be loaded.' : 'Loading…'}</p>
        </ModalBody>
      )}
    </Modal>
  );
}

function WizardSteps({ widget, projectId, webOrigin, onClose }: {
  widget: ProjectWidget;
  projectId: string;
  webOrigin?: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  /**
   * Only what the owner has touched, laid over the saved widget (review).
   *
   * A snapshot taken when the wizard opened, diffed against the live widget on
   * Next, would show a stale choice and then save it over a change another owner
   * made meanwhile. Holding only the controls the owner moved means an untouched
   * one always shows what is saved now and is never sent.
   */
  const [choices, setChoices] = useState<Choices>({});
  const choose = (patch: Choices): void => setChoices((current) => ({ ...current, ...patch }));
  /**
   * The saved sites as they were when the owner first changed the list. What is
   * saved is their additions and removals against that, applied to the list as
   * it is at save time, so a site another owner added meanwhile stays (review
   * round 2: the whole list, built on the old one, put it back).
   */
  const originsBase = useRef<string[] | null>(null);
  const chooseOrigins = (next: string[]): void => {
    if (originsBase.current === null) originsBase.current = widget.allowedOrigins;
    choose({ allowedOrigins: next });
  };
  const team = choices.teamMode ?? widget.teamMode;
  const teamSignIn = choices.teamSignIn ?? widget.teamSignIn ?? false;
  /** Who reports, as the first step asks it: the team, the public, or both (TOADO-585). */
  const reporters: 'team' | 'visitors' | 'both' = team ? 'team' : teamSignIn ? 'both' : 'visitors';
  const origins = choices.allowedOrigins ?? widget.allowedOrigins;
  const pointing = choices.visitorPointing ?? widget.visitorPointing;
  const recording = choices.visitorRecording ?? widget.visitorRecording;
  const [copied, setCopied] = useState(false);
  const project = useQuery({ queryKey: ['project', projectId], queryFn: () => api.getProject(projectId) });
  const plan = useBillingSummary(project.data?.companyId)?.plan;
  // An API from before `recordingOffered` offered recording, as the tabs did then.
  const recordingOffered = widget.recordingOffered !== false;
  // Unknown until the billing summary loads: not offered until it says so.
  const recordingOnPlan = plan !== undefined && plan !== 'free';
  const origin = webOrigin ?? window.location.origin;

  const save = useMutation({
    mutationFn: (body: UpdateProjectWidgetBody) => api.updateWidget(projectId, body),
    onSuccess: (updated) => qc.setQueryData(['widget', projectId], updated),
  });
  /**
   * One save at a time, in order. Allowing two sites from the check in quick
   * succession sends two whole lists; run side by side, the second could be
   * built from the list before the first landed and drop it (review).
   */
  const saving = useRef<Promise<unknown>>(Promise.resolve());
  const saveInTurn = (body: () => UpdateProjectWidgetBody | null): Promise<unknown> => {
    const run = saving.current.catch(() => undefined).then(() => {
      const next = body();
      return next ? save.mutateAsync(next) : undefined;
    });
    saving.current = run;
    return run;
  };

  /** Save what the owner chose on this step, only what they touched and only if it differs, then go on. */
  const next = async (): Promise<void> => {
    const body: UpdateProjectWidgetBody = {};
    if (step === 0) {
      if (choices.teamMode !== undefined && choices.teamMode !== widget.teamMode) body.teamMode = choices.teamMode;
      if (choices.teamSignIn !== undefined && choices.teamSignIn !== (widget.teamSignIn ?? false)) {
        body.teamSignIn = choices.teamSignIn;
      }
    }
    if (step === 1 && choices.allowedOrigins !== undefined) {
      const base = new Set(originsBase.current ?? widget.allowedOrigins);
      const chosen = new Set(choices.allowedOrigins);
      const merged = [
        ...widget.allowedOrigins.filter((site) => chosen.has(site) || !base.has(site)),
        ...choices.allowedOrigins.filter((site) => !base.has(site) && !widget.allowedOrigins.includes(site)),
      ];
      if (JSON.stringify(merged) !== JSON.stringify(widget.allowedOrigins)) body.allowedOrigins = merged;
    }
    if (step === 2) {
      if (choices.visitorPointing !== undefined && choices.visitorPointing !== widget.visitorPointing) {
        body.visitorPointing = choices.visitorPointing;
      }
      // Only where it can be offered: a plan without recording, or a server
      // that does not offer it, leaves the saved setting as the owner had it.
      if (recordingOffered && recordingOnPlan && choices.visitorRecording !== undefined
        && choices.visitorRecording !== widget.visitorRecording) {
        body.visitorRecording = choices.visitorRecording;
      }
    }
    if (Object.keys(body).length > 0) {
      try {
        await saveInTurn(() => body);
      } catch {
        return; // Said under the step; the owner can fix it and go on.
      }
    }
    // Saved, or nothing to save: from here the saved widget speaks for this
    // step. Only this step's choices go; another step's, made before Back,
    // stand until its own Next (review round 2).
    setChoices((current) => withoutStep(current, step));
    if (step === 1) originsBase.current = null;
    setStep((current) => Math.min(current + 1, STEPS.length - 1));
  };

  const sitesSet = origins.length > 0;
  /**
   * The widget's on switch as flipped, until that save settles. Held here
   * rather than read from the mutation, whose pending state arrives a tick
   * after the click: the switch flicked back to the saved value in between.
   */
  const [enabledChoice, setEnabledChoice] = useState<boolean | null>(null);
  const shownEnabled = enabledChoice ?? widget.enabled;
  const snippet = widgetSnippet(origin, widget.key);
  const stepScreen = STEP_SCREENS[step];

  return (
    <>
      <ModalBody>
        <ol className={styles.progress} aria-label="Setup steps">
          {STEPS.map((label, index) => (
            <li
              key={label}
              className={index === step ? styles.stepCurrent : index < step ? styles.stepDone : styles.step}
              aria-current={index === step ? 'step' : undefined}
            >
              {label}
            </li>
          ))}
        </ol>

        {step === 0 && (
          <fieldset className={styles.choices} data-toado-view={stepScreen}>
            <legend className={styles.question}>Who will report problems?</legend>
            <ChoiceCard
              name="widget-setup-reporters"
              checked={reporters === 'team'}
              onSelect={() => choose({ teamMode: true, teamSignIn: false })}
              icon={<Users size={18} aria-hidden />}
              title="Your own team"
              body="The widget is on a product your team uses. Reports are labelled as coming from your team, and a teammate can open their report in Toado straight away."
            />
            <ChoiceCard
              name="widget-setup-reporters"
              checked={reporters === 'visitors'}
              onSelect={() => choose({ teamMode: false, teamSignIn: false })}
              icon={<Globe size={18} aria-hidden />}
              title="Visitors to your site"
              body="The widget is on a site the public uses. Anyone can report a problem, with no account."
            />
            {/* Only where the server offers Team pins, which is what the team
                gets here (TOADO-585); without them this would be the same as
                Visitors. A widget already set this way still shows it. */}
            {(widget.teamPinsOffered || reporters === 'both') && (
              <ChoiceCard
                name="widget-setup-reporters"
                checked={reporters === 'both'}
                onSelect={() => choose({ teamMode: false, teamSignIn: true })}
                icon={<UsersRound size={18} aria-hidden />}
                title="Both"
                body="The public reports as visitors, with everything a public site needs, and your team signs in from the widget to see the page's pins and add their own."
              />
            )}
          </fieldset>
        )}

        {step === 1 && (
          <div className={styles.stack} data-toado-view={stepScreen}>
            <h3 className={styles.question}>Where will the widget go?</h3>
            <p className={styles.help}>
              Add each site the snippet goes on, starting with https:// (or http:// for a site on
              your own computer). Reports are taken only from these sites, and pointing at the page
              and recording need at least one.
            </p>
            <AllowedSitesEditor
              origins={origins}
              anySiteNow={isLegacyAnySite(widget)}
              onChange={(patch) => { if (patch.allowedOrigins) chooseOrigins(patch.allowedOrigins); }}
            />
            {!sitesSet && (
              <p className={styles.warning}>
                {/* Saved on with none: from before the rule. Saved on with sites, all taken off here: refused on Next. Off: cannot be turned on yet (review round 3). */}
                <AlertTriangle size={14} aria-hidden /> {isLegacyAnySite(widget)
                  ? 'With no sites, any site can send reports, and pointing and recording stay off. Add yours here or later under Security.'
                  : widget.enabled
                    ? 'The widget is on, so it needs a site: keep one, or turn the widget off before removing its last one.'
                    : 'The widget cannot be turned on until it lists a site. Add the site you put the snippet on.'}
              </p>
            )}
          </div>
        )}

        {step === 2 && (
          <fieldset className={styles.choices} data-toado-view={stepScreen}>
            <legend className={styles.question}>What can people do?</legend>
            {/* Side by side, each with a drawing of what it does (TOADO-596). */}
            <div className={styles.waysFrame}>
            <div className={styles.ways}>
              <WayCard
                way="tell"
                title="Tell us about it"
                body="Describe the problem in a short form. Always on: it is the one way that works on every browser and phone, and where pointing or recording can’t run."
                checked
                disabled
              />
              <WayCard
                way="point"
                title="Point to the problem"
                body="Select the part of the page that is wrong, then write up what happened."
                checked={pointing}
                onChange={(on) => choose({ visitorPointing: on })}
              >
                {pointing && !sitesSet && <Need text="Needs an allowed site, from the last step." />}
              </WayCard>
              {recordingOffered && (
                <WayCard
                  way="record"
                  title="Record what they can see"
                  body="Record the tab and pin each problem, talking it through if they like. Desktop Chrome and Edge only. Some sites need a header or iframe change first; the Capture tab’s Recording section says what."
                  checked={recording && recordingOnPlan}
                  disabled={!recordingOnPlan}
                  onChange={(on) => choose({ visitorRecording: on })}
                >
                  {!recordingOnPlan && <Need text="Your workspace is on the Free plan, which does not include recording." />}
                  {recordingOnPlan && recording && !sitesSet && <Need text="Needs an allowed site, from the last step." />}
                </WayCard>
              )}
            </div>
            </div>
          </fieldset>
        )}

        {step === 3 && (
          <div className={styles.stack} data-toado-view={stepScreen}>
            <h3 className={styles.question}>Add it to your site</h3>
            <p className={styles.help}>
              Paste this into your site&rsquo;s HTML, just before the closing body tag, on every page
              where you want the button.
            </p>
            <pre className={styles.snippet}>{snippet}</pre>
            <div>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={copied ? <Check size={14} /> : <Copy size={14} />}
                onClick={async () => {
                  if (await copyToClipboard(snippet)) {
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 1400);
                  }
                }}
              >
                {copied ? 'Copied' : 'Copy snippet'}
              </Button>
            </div>
            <p className={styles.help}>
              Then open a page on your site and check. This works before the widget is on.
            </p>
            <WidgetVerificationPanel
              widget={widget}
              projectId={projectId}
              stagedOrigins={widget.allowedOrigins}
              savesImmediately
              onStageOrigin={(allowed) => {
                // Built from the list as it stands when this save's turn comes,
                // so a second Allow pressed before the first landed keeps both.
                void saveInTurn(() => {
                  const current = qc.getQueryData<ProjectWidget>(['widget', projectId])?.allowedOrigins
                    ?? widget.allowedOrigins;
                  return current.includes(allowed) ? null : { allowedOrigins: [...current, allowed] };
                }).catch(() => undefined);
              }}
            />
          </div>
        )}

        {step === 4 && (
          <div className={styles.stack} data-toado-view={stepScreen}>
            <h3 className={styles.question}>{widget.enabled ? 'The widget is on' : 'Turn it on'}</h3>
            {/* The switch on the page that says to turn it on (Jason, 10 Oct),
                the same large Off/On as Install's. Saved as it is flipped. */}
            <div className={styles.turnOn} data-on={shownEnabled}>
              <span className={styles.turnOnTitle} id="widget-setup-enabled-title">Widget</span>
              <BigSwitch
                labelledBy="widget-setup-enabled-title"
                checked={shownEnabled}
                // Not turned on without a site to be on (t-a4DgDT).
                disabled={save.isPending || (!shownEnabled && widget.allowedOrigins.length === 0)}
                onChange={(on) => {
                  setEnabledChoice(on);
                  save.mutate({ enabled: on }, { onSettled: () => setEnabledChoice(null) });
                }}
              />
            </div>
            {!shownEnabled && widget.allowedOrigins.length === 0 && (
              <p className={styles.warning} role="status" data-testid="widget-setup-needs-site">
                <AlertTriangle size={14} aria-hidden /> {NEEDS_A_SITE}
                <Button size="sm" variant="secondary" onClick={() => setStep(1)}>Add your site</Button>
              </p>
            )}
            <dl className={styles.summary}>
              <dt>Who reports</dt>
              {/* Each audience with a check or a cross (TOADO-597). */}
              <dd className={styles.who}>
                <Reporter label="Visitors" on={!widget.teamMode} />
                <Reporter label="Your team" on={widget.teamMode || widget.teamSignIn === true} />
              </dd>
              <dt>Sites</dt>
              {/* On from before a site was required, it takes reports from any site; off, it has none yet. */}
              <dd>{widget.allowedOrigins.length > 0 ? widget.allowedOrigins.join(', ') : widget.enabled ? 'Any site' : 'None yet'}</dd>
              <dt>People can</dt>
              <dd>
                {/* What will work, not only what is switched on: pointing and
                    recording need an allowed site, and recording a plan and a
                    server that offer it (review). */}
                {['Tell us about it',
                  ...(widget.visitorPointing && widget.allowedOrigins.length > 0 ? ['point to the problem'] : []),
                  ...(widget.visitorRecording && widget.allowedOrigins.length > 0 && recordingOffered && recordingOnPlan
                    ? ['record what they can see'] : []),
                ].join(', ')}
                {(widget.visitorPointing || widget.visitorRecording) && widget.allowedOrigins.length === 0 && (
                  <span className={styles.help}>
                    {widget.visitorPointing && widget.visitorRecording
                      ? 'Pointing and recording start'
                      : widget.visitorPointing ? 'Pointing starts' : 'Recording starts'} once you add an allowed site.
                  </span>
                )}
              </dd>
            </dl>
            <p className={styles.help}>
              {widget.enabled
                ? 'People see the button on your site. Change anything later in the widget settings.'
                : 'Turning it on shows the button on your site and starts taking reports. You can leave it off and turn it on later under Install.'}
            </p>
          </div>
        )}

        {step === 5 && (
          <div data-toado-view={stepScreen}>
            <WidgetTestReport projectId={projectId} widget={widget} enabled={shownEnabled} onTurnOn={() => setStep(4)} />
          </div>
        )}

        {save.isError && (
          <p className={styles.warning} role="alert">
            <AlertTriangle size={14} aria-hidden />
            {save.error instanceof Error ? save.error.message : 'That could not be saved.'}
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        {step > 0 && (
          <Button variant="ghost" onClick={() => { save.reset(); setStep(step - 1); }} disabled={save.isPending}>
            Back
          </Button>
        )}
        {step < STEPS.length - 1 && (
          <Button variant="primary" onClick={() => { void next(); }} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Next'}
          </Button>
        )}
        {step === STEPS.length - 1 && (
          <Button variant="primary" onClick={onClose} disabled={save.isPending}>Done</Button>
        )}
      </ModalFooter>
    </>
  );
}

/** The fields each step sets, so finishing a step lets go of its choices and no other's. */
const STEP_FIELDS: Record<number, Array<keyof Choices>> = {
  0: ['teamMode', 'teamSignIn'],
  1: ['allowedOrigins'],
  2: ['visitorPointing', 'visitorRecording'],
};

function withoutStep(choices: Choices, step: number): Choices {
  const next = { ...choices };
  for (const field of STEP_FIELDS[step] ?? []) delete next[field];
  return next;
}

/** The controls the owner has touched, by the widget field each sets. */
interface Choices {
  teamMode?: boolean;
  teamSignIn?: boolean;
  allowedOrigins?: string[];
  visitorPointing?: boolean;
  visitorRecording?: boolean;
}

function ChoiceCard({ name, checked, onSelect, icon, title, body }: {
  name: string;
  checked: boolean;
  onSelect: () => void;
  icon: ReactNode;
  title: string;
  body: string;
}) {
  return (
    <label className={checked ? styles.cardSelected : styles.card}>
      <input type="radio" name={name} checked={checked} onChange={onSelect} className={styles.cardInput} />
      <span className={styles.cardIcon}>{icon}</span>
      <span>
        <strong>{title}</strong>
        <span className={styles.help}>{body}</span>
      </span>
    </label>
  );
}

/**
 * One way to report, as a column: its drawing, what it is, and whether it is
 * on. The whole card is the checkbox's label, so a click anywhere on it
 * switches it; the box itself stays for keyboards and screen readers.
 */
function WayCard({ way, title, body, checked, disabled = false, onChange, children }: {
  way: ReportWay;
  title: string;
  body: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (on: boolean) => void;
  /** What it still needs, under its description. */
  children?: ReactNode;
}) {
  return (
    <label className={disabled ? styles.wayDisabled : checked ? styles.wayOn : styles.way}>
      <span className={styles.wayArt}><ReportWayArt way={way} /></span>
      <span className={styles.wayHead}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange?.(event.target.checked)}
        />
        <strong>{title}</strong>
      </span>
      <span className={styles.help}>{body}</span>
      {children}
    </label>
  );
}

/** "Visitors" or "Your team", with a check when they can report and a cross when not. */
function Reporter({ label, on }: { label: string; on: boolean }) {
  return (
    <span className={on ? styles.whoOn : styles.whoOff} data-testid={`who-${label === 'Visitors' ? 'visitors' : 'team'}`}>
      {label}
      {on ? <Check size={14} aria-hidden /> : <X size={14} aria-hidden />}
      <span className={styles.srOnly}>{on ? ': yes' : ': no'}</span>
    </span>
  );
}

function Need({ text }: { text: string }) {
  return (
    <span className={styles.need}>
      <AlertTriangle size={13} aria-hidden /> {text}
    </span>
  );
}
